import React, { FC, ReactElement, useEffect } from 'react';
import {
  Alert,
  Button,
  Collapse,
  ConfigProvider,
  Flex,
  Tooltip,
  Typography,
} from '@duxton-web/ui';

import DefaultRolloutBlock from './RolloutBlock/DefaultRolloutBlock';
import { SecondaryText } from '@/common/components';
import { validateValue } from '@/common/StaticValueInput/ajv';
import { useVariable } from '@/contexts/VariableContext';
import { useGetCanaryPilotTechFamilies } from '@/hooks/usePlannerQueries/canary';
import { Rollout, RolloutTarget } from '@/lib/PlannerDTOs';

interface DefaultTargetProps {
  rollout: Rollout;
  targetIndexToHighlight: number;
  activePanels: string[];
  handlePanelToggle: (key: string) => void;
  disabled: boolean;
  enableReorder: boolean;
  handleDefaultTargetChange: (rolloutTarget: RolloutTarget) => void;
  isDiff: boolean;
  oldDefaultTarget: RolloutTarget;
}

const DefaultTarget: FC<DefaultTargetProps> = ({
  rollout,
  targetIndexToHighlight,
  activePanels,
  handlePanelToggle,
  disabled,
  enableReorder,
  handleDefaultTargetChange,
  isDiff,
  oldDefaultTarget,
}) => {
  const variable = useVariable();

  // When a variable has both canary enabled AND mandatory facets configured,
  // the default value cannot be canary-rolled out (the canary-default rollout
  // target must be constraint-free, which violates the mandatory-facets rule).
  // Disable editing to prevent the user from creating a diff that the backend
  // will reject. Show a tooltip explaining the restriction.
  //
  // IMPORTANT: the lock only applies when the canary feature is actually active
  // for this variable. If EnableCanaryRollout=false (pilot TF list is empty) or
  // this variable's TF is not in the enrolled pilot, approving a default-value
  // diff will go through the normal (non-canary) path and the mandatory-facets
  // restriction does not apply.
  const { data: canaryConfig } = useGetCanaryPilotTechFamilies();
  const isCanaryFeatureActiveForVariable =
    canaryConfig?.usePilotTechFamilies === false
      ? true
      : !!canaryConfig?.techFamilies?.includes(variable?.techFamily);
  const canaryEnabled = variable?.metadata?.canaryConfig?.enabled;
  const hasMandatoryFacets =
    (variable?.metadata?.mandatoryFacets?.length ?? 0) > 0;
  const isDefaultLockedForCanary =
    !isDiff &&
    isCanaryFeatureActiveForVariable &&
    !!canaryEnabled &&
    hasMandatoryFacets;

  useEffect(() => {
    if (targetIndexToHighlight === -1)
      document.getElementById(`rollot-editor-target--1`).focus();
  }, [targetIndexToHighlight]);

  function renderDefaultTarget(): ReactElement {
    const isValid = !validateValue(variable, {
      value: rollout.default.value,
      string: rollout.default.string,
      script: rollout.default.script,
    })?.length;

    return (
      <ConfigProvider
        theme={{
          components: {
            Alert: {
              defaultPadding: '14px 14px',
            },
          },
        }}
      >
        <Flex vertical gap={16}>
          <Alert
            showIcon
            type="info"
            message="In case the user doesn't match any of the experiments or rollouts, then they will receive the default value below."
          />
          <div
            tabIndex={-1}
            id={`rollot-editor-target--1`}
            className={
              targetIndexToHighlight === -1
                ? isValid
                  ? `blink-bg`
                  : 'blink-bg-no-slotting'
                : ``
            }
          >
            <ConfigProvider
              theme={{
                components: {
                  Collapse: {
                    headerBg: '#FFF',
                  },
                },
              }}
            >
              <Collapse
                className={`rollout-editor-target ${
                  isValid ? '' : 'invalid-rollout-target'
                }`}
                activeKey={activePanels}
                onChange={() => handlePanelToggle('-1')}
                expandIconPosition="end"
              >
                <Collapse.Panel
                  key={'-1'}
                  header={
                    <Flex align="center" justify="space-between">
                      <Flex vertical>
                        <Typography.Text strong>Default Value</Typography.Text>
                        <SecondaryText>
                          We will return this value if the user doesn&apos;t
                          match any experiments or rollouts.
                        </SecondaryText>
                        {isDefaultLockedForCanary && (
                          <SecondaryText>
                            <Typography.Text type="warning">
                              Default value cannot be changed while canary and
                              mandatory facets are both enabled — the canary
                              shadow target cannot carry mandatory facet
                              constraints. Remove mandatory facets to enable
                              this.
                            </Typography.Text>
                          </SecondaryText>
                        )}
                      </Flex>

                      <Button size="small" ghost disabled />
                    </Flex>
                  }
                >
                  <Tooltip
                    title={
                      isDefaultLockedForCanary
                        ? 'Default value editing is disabled: this variable has canary enabled and mandatory facets configured. The canary-default rollout target cannot carry mandatory facet constraints. Remove mandatory facets from variable settings to edit the default value.'
                        : undefined
                    }
                  >
                    <DefaultRolloutBlock
                      value={rollout.default}
                      variable={variable}
                      disabled={
                        disabled || enableReorder || isDefaultLockedForCanary
                      }
                      onChange={handleDefaultTargetChange}
                      isDiff={isDiff}
                      oldDefaultTarget={oldDefaultTarget}
                    />
                  </Tooltip>
                </Collapse.Panel>
              </Collapse>
            </ConfigProvider>
          </div>
        </Flex>
      </ConfigProvider>
    );
  }
  return renderDefaultTarget();
};

export default DefaultTarget;
